import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2, CloudDownload, FileSpreadsheet, Loader2, RefreshCw, Search,
  Stethoscope, Trash2, Upload, Users, Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  PanelTableCard, PanelTableHeader, PanelTableFrame, PanelTableHeadRow, PanelTableHead,
  PanelTableBody, PanelTableRow, PanelTableCell, PanelTableEmpty, PanelTablePagination,
  TableHeader,
} from "@/components/ui/panel-table";
import { EntityStatPill } from "@/components/companies/entity-ui";
import {
  CIRO_SHEET_HINTS,
  CEVDET_DEFAULT_SHEETS,
  formatCiroDate,
  formatCiroMoney,
  parseDataSheetRows,
  parseTeamListWorkbook,
  readWorkbookFromFile,
  normalizeImportRows,
  summarizeImportDateRange,
} from "@/utils/ciroHelpers";
import CiroDashboardPanel from "@/components/finance/CiroDashboardPanel";
import CiroOperationDetailModal from "@/components/finance/CiroOperationDetailModal";

const IMPORT_STEPS = [
  { id: "team", label: "1. Ekip Listesi", hint: "Hoca ve ekip listesi .xlsx" },
  { id: "workbook", label: "2. Muhasebe Excel", hint: "CİRO TAKİP + ATAŞEHİR birlikte" },
];

const CiroTrackingPage = ({
  apiBase, isDark, textClass, labelClass, cardClass, inputClass,
  companies = [], isSuperAdmin = false, userCompanyId = null,
}) => {
  const teamFileRef = useRef(null);
  const ciroFileRef = useRef(null);
  const [activePanel, setActivePanel] = useState("dashboard");
  const [loadingOperations, setLoadingOperations] = useState(false);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [importing, setImporting] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [trendPeriod, setTrendPeriod] = useState("monthly");
  const [dashboardData, setDashboardData] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [clearing, setClearing] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearAllDb, setClearAllDb] = useState(false);
  const [companyFilter, setCompanyFilter] = useState(isSuperAdmin ? "all" : String(userCompanyId || ""));
  const [doctorFilter, setDoctorFilter] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [operations, setOperations] = useState([]);
  const [operationsTotal, setOperationsTotal] = useState(0);
  const [summary, setSummary] = useState({});
  const [payments, setPayments] = useState([]);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [doctors, setDoctors] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [sheetSources, setSheetSources] = useState([]);
  const [batches, setBatches] = useState([]);
  const [importStep, setImportStep] = useState("team");
  const [importCompanyId, setImportCompanyId] = useState(isSuperAdmin ? "" : String(userCompanyId || ""));
  const [importDoctorId, setImportDoctorId] = useState("");
  const [importHospitalId, setImportHospitalId] = useState("");
  const [teamPreview, setTeamPreview] = useState(null);
  const [opsPreview, setOpsPreview] = useState(null);
  const [payPreview, setPayPreview] = useState(null);
  const [sheetForm, setSheetForm] = useState({
    label: CEVDET_DEFAULT_SHEETS.operations.label,
    sheet_url: CEVDET_DEFAULT_SHEETS.operations.sheet_url,
    sheet_type: "ciro_takip",
    doctor_id: "",
    hospital_id: "",
  });
  const [googleStatus, setGoogleStatus] = useState(null);

  const defaultCompanyId = useMemo(() => {
    if (!isSuperAdmin && userCompanyId) return String(userCompanyId);
    if (companyFilter && companyFilter !== "all") return companyFilter;
    return companies[0]?.id ? String(companies[0].id) : "";
  }, [isSuperAdmin, userCompanyId, companyFilter, companies]);

  const scopedDoctors = useMemo(() => {
    if (!isSuperAdmin || companyFilter === "all") return doctors;
    return doctors.filter((d) => String(d.company_id) === String(companyFilter));
  }, [doctors, companyFilter, isSuperAdmin]);

  const fetchDoctorsHospitals = useCallback(async () => {
    try {
      const params = {};
      if (isSuperAdmin && companyFilter && companyFilter !== "all") params.company_id = companyFilter;
      const [docRes, hospRes] = await Promise.all([
        axios.get(`${apiBase}/doctors`, { params }),
        axios.get(`${apiBase}/hospitals`, { params }),
      ]);
      setDoctors(docRes.data?.doctors || []);
      setHospitals(hospRes.data?.hospitals || []);
    } catch {
      setDoctors([]);
      setHospitals([]);
    }
  }, [apiBase, companyFilter, isSuperAdmin]);

  const fetchDashboard = useCallback(async () => {
    setLoadingDashboard(true);
    try {
      const params = {
        dashboard: 1,
        trend_period: trendPeriod,
        search: searchTerm,
        doctor_id: doctorFilter,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      };
      if (isSuperAdmin && companyFilter !== "all") params.company_id = companyFilter;
      const res = await axios.get(`${apiBase}/ciro`, { params });
      setDashboardData(res.data || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Dashboard yüklenemedi");
      setDashboardData(null);
    } finally {
      setLoadingDashboard(false);
    }
  }, [apiBase, companyFilter, doctorFilter, endDate, isSuperAdmin, searchTerm, startDate, trendPeriod]);

  const openOperationDetail = async (operationId) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailData(null);
    try {
      const res = await axios.get(`${apiBase}/ciro`, { params: { operation_id: operationId } });
      setDetailData(res.data || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Detay yüklenemedi");
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchOperations = useCallback(async () => {
    setLoadingOperations(true);
    try {
      const params = {
        operations: 1,
        page,
        limit: pageSize,
        search: searchTerm,
        doctor_id: doctorFilter,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      };
      if (isSuperAdmin && companyFilter !== "all") params.company_id = companyFilter;
      const res = await axios.get(`${apiBase}/ciro`, { params });
      setOperations(res.data?.items || []);
      setOperationsTotal(res.data?.total || 0);
      setSummary(res.data?.summary || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ciro kayıtları yüklenemedi");
      setOperations([]);
      setOperationsTotal(0);
    } finally {
      setLoadingOperations(false);
    }
  }, [apiBase, companyFilter, doctorFilter, endDate, isSuperAdmin, page, pageSize, searchTerm, startDate]);

  const fetchPayments = useCallback(async () => {
    setLoadingPayments(true);
    try {
      const params = {
        hospital_payments: 1,
        page,
        limit: pageSize,
        search: searchTerm,
        doctor_id: doctorFilter,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      };
      if (isSuperAdmin && companyFilter !== "all") params.company_id = companyFilter;
      const res = await axios.get(`${apiBase}/ciro`, { params });
      setPayments(res.data?.items || []);
      setPaymentsTotal(res.data?.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hastane ödemeleri yüklenemedi");
      setPayments([]);
      setPaymentsTotal(0);
    } finally {
      setLoadingPayments(false);
    }
  }, [apiBase, companyFilter, doctorFilter, endDate, isSuperAdmin, page, pageSize, searchTerm, startDate]);

  const fetchMeta = useCallback(async () => {
    try {
      const params = {};
      if (isSuperAdmin && companyFilter !== "all") params.company_id = companyFilter;
      const [srcRes, batchRes, googleRes] = await Promise.all([
        axios.get(`${apiBase}/ciro`, { params: { ...params, sheet_sources: 1 } }),
        axios.get(`${apiBase}/ciro`, { params: { ...params, batches: 1 } }),
        axios.get(`${apiBase}/ciro`, { params: { google_status: 1 } }),
      ]);
      setSheetSources(srcRes.data?.items || []);
      setBatches(batchRes.data?.items || []);
      setGoogleStatus(googleRes.data || null);
    } catch {
      setSheetSources([]);
      setBatches([]);
      setGoogleStatus(null);
    }
  }, [apiBase, companyFilter, isSuperAdmin]);

  useEffect(() => { fetchDoctorsHospitals(); }, [fetchDoctorsHospitals]);
  useEffect(() => { fetchMeta(); }, [fetchMeta]);

  useEffect(() => {
    if (activePanel === "dashboard") fetchDashboard();
  }, [trendPeriod, activePanel, fetchDashboard]);

  useEffect(() => {
    if (activePanel === "operations") fetchOperations();
  }, [activePanel, fetchOperations]);

  useEffect(() => {
    if (!importCompanyId && defaultCompanyId) setImportCompanyId(defaultCompanyId);
  }, [defaultCompanyId, importCompanyId]);

  const handleSearch = () => {
    setPage(1);
    setSearchTerm(searchInput.trim());
  };

  const runClearData = async () => {
    const companyId = Number(importCompanyId || defaultCompanyId);
    if (!clearAllDb && (!companyId || companyId <= 0)) {
      toast.warning("Önce firma seçin veya tüm veritabanını sıfırlayın");
      return;
    }

    setClearing(true);
    try {
      const payload = clearAllDb
        ? { clear_all: true }
        : { company_id: companyId };

      const res = await axios.post(`${apiBase}/ciro?action=clear_data`, payload);
      toast.success(
        `${res.data?.operations || 0} işlem, ${res.data?.payments || 0} ödeme, ${res.data?.batches || 0} import kaydı silindi`
      );
      setOpsPreview(null);
      setPayPreview(null);
      setClearDialogOpen(false);
      fetchMeta();
      fetchDashboard();
      fetchOperations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Veriler silinemedi");
    } finally {
      setClearing(false);
    }
  };

  const runTeamImport = async () => {
    if (!teamPreview?.rows?.length) {
      toast.warning("Önce ekip listesi dosyasını yükleyin");
      return;
    }
    setImporting(true);
    try {
      const res = await axios.post(`${apiBase}/ciro?action=import_team`, {
        company_id: Number(importCompanyId || defaultCompanyId),
        rows: teamPreview.rows,
      });
      toast.success(`Ekip listesi aktarıldı: ${res.data?.created?.hospitals || 0} hastane, ${res.data?.created?.doctors || 0} doktor`);
      await fetchDoctorsHospitals();
      setImportStep("workbook");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ekip listesi aktarılamadı");
    } finally {
      setImporting(false);
    }
  };

  const runWorkbookImport = async () => {
    if (!opsPreview?.rows?.length) {
      toast.warning("Ciro takip verisi yok");
      return;
    }
    if (!importDoctorId) {
      toast.warning("Doktor seçin");
      return;
    }
    setImporting(true);
    try {
      const res = await axios.post(`${apiBase}/ciro?action=import_workbook`, {
        company_id: Number(importCompanyId || defaultCompanyId),
        doctor_id: Number(importDoctorId),
        hospital_id: importHospitalId ? Number(importHospitalId) : null,
        source_label: opsPreview.fileName || "Muhasebe Excel",
        operation_rows: normalizeImportRows(opsPreview.rows),
        payment_rows: normalizeImportRows(payPreview?.rows || []),
      });
      const link = res.data?.link || {};
      toast.success(
        `${res.data?.operations?.inserted || 0} işlem, ${res.data?.payments?.inserted || 0} ödeme eklendi · ${link.linked || 0} eşleşme`
      );
      fetchMeta();
      setActivePanel("dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Muhasebe dosyası aktarılamadı");
    } finally {
      setImporting(false);
    }
  };

  const runOperationsImport = async () => {
    if (!opsPreview?.rows?.length) {
      toast.warning("Ciro takip verisi yok");
      return;
    }
    if (!importDoctorId) {
      toast.warning("Doktor seçin");
      return;
    }
    setImporting(true);
    try {
      const res = await axios.post(`${apiBase}/ciro?action=import_operations`, {
        company_id: Number(importCompanyId || defaultCompanyId),
        doctor_id: Number(importDoctorId),
        hospital_id: importHospitalId ? Number(importHospitalId) : null,
        source_label: opsPreview.sheetName,
        rows: normalizeImportRows(opsPreview.rows),
      });
      toast.success(`${res.data?.inserted || 0} işlem eklendi, ${res.data?.skipped || 0} atlandı`);
      setImportStep("payments");
      fetchMeta();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ciro takip aktarılamadı");
    } finally {
      setImporting(false);
    }
  };

  const runPaymentsImport = async () => {
    if (!payPreview?.rows?.length) {
      toast.warning("Hastane ödeme verisi yok");
      return;
    }
    if (!importDoctorId) {
      toast.warning("Doktor seçin");
      return;
    }
    setImporting(true);
    try {
      const res = await axios.post(`${apiBase}/ciro?action=import_hospital_payments`, {
        company_id: Number(importCompanyId || defaultCompanyId),
        doctor_id: Number(importDoctorId),
        hospital_id: importHospitalId ? Number(importHospitalId) : null,
        source_label: payPreview.sheetName,
        rows: normalizeImportRows(payPreview.rows),
      });
      toast.success(`${res.data?.inserted || 0} ödeme eklendi, ${res.data?.skipped || 0} atlandı`);
      fetchMeta();
      setActivePanel("operations");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hastane ödemeleri aktarılamadı");
    } finally {
      setImporting(false);
    }
  };

  const handleTeamFile = async (file) => {
    if (!file) return;
    try {
      const wb = await readWorkbookFromFile(file);
      const rows = parseTeamListWorkbook(wb);
      setTeamPreview({ fileName: file.name, rows, columns: rows[0]?.length || 0 });
      toast.success(`${rows.length} satır okundu`);
    } catch {
      toast.error("Ekip listesi dosyası okunamadı");
    }
  };

  const handleCiroFile = async (file) => {
    if (!file) return;
    try {
      const wb = await readWorkbookFromFile(file);
      const ops = parseDataSheetRows(wb, CIRO_SHEET_HINTS.operations);
      const pays = parseDataSheetRows(wb, CIRO_SHEET_HINTS.hospitalPayments);
      setOpsPreview({ fileName: file.name, ...ops, count: ops.rows.length });
      setPayPreview({ fileName: file.name, ...pays, count: pays.rows.length });
      toast.success(`Ciro: ${ops.rows.length} · Ödeme: ${pays.rows.length} satır`);
    } catch {
      toast.error("Muhasebe dosyası okunamadı");
    }
  };

  const saveSheetSource = async () => {
    if (!sheetForm.doctor_id || !sheetForm.label || !sheetForm.sheet_url) {
      toast.warning("Doktor, etiket ve sheet URL zorunlu");
      return;
    }
    setImporting(true);
    try {
      await axios.post(`${apiBase}/ciro?action=save_sheet_source`, {
        company_id: Number(importCompanyId || defaultCompanyId),
        ...sheetForm,
        doctor_id: Number(sheetForm.doctor_id),
        hospital_id: sheetForm.hospital_id ? Number(sheetForm.hospital_id) : null,
      });
      toast.success("Google Sheet kaynağı kaydedildi");
      fetchMeta();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kaynak kaydedilemedi");
    } finally {
      setImporting(false);
    }
  };

  const syncSheet = async (sourceId) => {
    setSyncingId(sourceId);
    try {
      const res = await axios.post(`${apiBase}/ciro?action=sync_sheet`, { source_id: sourceId });
      toast.success(`Senkron tamam: ${res.data?.inserted || 0} yeni, ${res.data?.skipped || 0} mevcut`);
      fetchMeta();
      if (activePanel === "dashboard") fetchDashboard();
      if (activePanel === "operations") fetchOperations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Google Sheet senkronu başarısız");
    } finally {
      setSyncingId(null);
    }
  };

  const opsDateRange = useMemo(() => summarizeImportDateRange(opsPreview?.rows), [opsPreview?.rows]);
  const payDateRange = useMemo(() => summarizeImportDateRange(payPreview?.rows), [payPreview?.rows]);

  const cevdetDoctor = useMemo(
    () => doctors.find((d) => /cevdet/i.test(d.ad_soyad || "")),
    [doctors]
  );

  const atasehirHospital = useMemo(
    () => hospitals.find((h) => /ataşehir|atasehir/i.test(h.ad || "")),
    [hospitals]
  );

  useEffect(() => {
    if (cevdetDoctor && !importDoctorId) setImportDoctorId(String(cevdetDoctor.id));
    if (atasehirHospital && !importHospitalId) setImportHospitalId(String(atasehirHospital.id));
    if (cevdetDoctor && !sheetForm.doctor_id) {
      setSheetForm((prev) => ({ ...prev, doctor_id: String(cevdetDoctor.id) }));
    }
    if (atasehirHospital && !sheetForm.hospital_id) {
      setSheetForm((prev) => ({ ...prev, hospital_id: String(atasehirHospital.id) }));
    }
  }, [cevdetDoctor, atasehirHospital, importDoctorId, importHospitalId, sheetForm.doctor_id, sheetForm.hospital_id]);

  return (
    <div className="space-y-4">
      <div className={`rounded-xl border p-4 ${cardClass}`}>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className={`text-lg font-semibold flex items-center gap-2 ${textClass}`}>
              <Wallet className="w-5 h-5 text-emerald-500" />
              Ciro Takibi
            </h2>
            <p className={`text-xs mt-1 ${labelClass}`}>
              Excel toplu aktarım · Google Sheet günlük senkron · Hastane / doktor bağlantılı işlem kayıtları
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { id: "dashboard", label: "Özet" },
              { id: "operations", label: "İşlemler" },
              { id: "import", label: "İçe Aktar" },
              { id: "sync", label: "Google Sheet" },
            ].map((tab) => (
              <Button
                key={tab.id}
                type="button"
                size="sm"
                variant={activePanel === tab.id ? "default" : "outline"}
                onClick={() => { setActivePanel(tab.id); setPage(1); }}
                className={activePanel === tab.id ? "bg-emerald-600 hover:bg-emerald-700" : ""}
              >
                {tab.label}
              </Button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mt-4">
          <EntityStatPill label="Doktor" value={scopedDoctors.length} tone="sky" isDark={isDark} />
          <EntityStatPill label="Hastane" value={hospitals.length} tone="indigo" isDark={isDark} />
          <EntityStatPill label="İşlem Kaydı" value={dashboardData?.kpis?.total_operations || summary.total_records || operationsTotal || 0} tone="violet" isDark={isDark} />
          <EntityStatPill label="Toplam Ciro" value={formatCiroMoney(dashboardData?.kpis?.total_ciro || summary.total_payment_sum)} tone="emerald" isDark={isDark} />
        </div>
      </div>

      {(activePanel === "dashboard" || activePanel === "operations") && (
        <div className={`rounded-xl border p-3 ${cardClass}`}>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-2 items-end">
            {isSuperAdmin && (
              <div>
                <Label className={`text-xs ${labelClass}`}>Firma</Label>
                <Select value={companyFilter} onValueChange={(v) => { setCompanyFilter(v); setPage(1); }}>
                  <SelectTrigger className={`h-8 text-xs ${inputClass}`}><SelectValue /></SelectTrigger>
                  <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                    <SelectItem value="all">Tümü</SelectItem>
                    {companies.filter((c) => c.status === 1).map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>{c.ad}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className={`text-xs ${labelClass}`}>Doktor</Label>
              <Select value={doctorFilter} onValueChange={(v) => { setDoctorFilter(v); setPage(1); }}>
                <SelectTrigger className={`h-8 text-xs ${inputClass}`}><SelectValue /></SelectTrigger>
                <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                  <SelectItem value="all">Tümü</SelectItem>
                  {scopedDoctors.map((d) => (
                    <SelectItem key={d.id} value={String(d.id)}>{d.ad_soyad}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className={`text-xs ${labelClass}`}>Başlangıç</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={`h-8 text-xs ${inputClass}`} />
            </div>
            <div>
              <Label className={`text-xs ${labelClass}`}>Bitiş</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={`h-8 text-xs ${inputClass}`} />
            </div>
            <div className="col-span-2 flex gap-2">
              <Input
                placeholder="Hasta, agent, işlem ara..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className={`h-8 text-xs ${inputClass}`}
              />
              <Button type="button" size="sm" onClick={handleSearch} className="h-8 shrink-0">
                <Search className="w-4 h-4" />
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={() => (activePanel === "dashboard" ? fetchDashboard() : fetchOperations())} className="h-8 shrink-0">
                <RefreshCw className={`w-4 h-4 ${(activePanel === "dashboard" ? loadingDashboard : loadingOperations) ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>
        </div>
      )}

      {activePanel === "dashboard" && (
        <div className={`rounded-xl border p-4 ${cardClass}`}>
          <CiroDashboardPanel
            data={dashboardData}
            loading={loadingDashboard}
            isDark={isDark}
            textClass={textClass}
            labelClass={labelClass}
            trendPeriod={trendPeriod}
            onTrendPeriodChange={(period) => {
              setTrendPeriod(period);
            }}
          />
        </div>
      )}

      {activePanel === "operations" && (
        <PanelTableCard className={cardClass}>
          <PanelTableHeader title="Ciro İşlemleri" description={`${operationsTotal} kayıt`} isDark={isDark} textClass={textClass} labelClass={labelClass} accent="teal" />
          <PanelTableFrame
            isDark={isDark}
            footer={(
              <PanelTablePagination
                isDark={isDark}
                page={page}
                pageSize={pageSize}
                total={operationsTotal}
                totalPages={Math.max(1, Math.ceil(operationsTotal / pageSize))}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                loading={loadingOperations}
                rangeStart={operationsTotal === 0 ? 0 : (page - 1) * pageSize + 1}
                rangeEnd={operationsTotal === 0 ? 0 : Math.min(page * pageSize, operationsTotal)}
              />
            )}
          >
            <TableHeader>
              <PanelTableHeadRow isDark={isDark}>
                <PanelTableHead isDark={isDark}>Tarih</PanelTableHead>
                <PanelTableHead isDark={isDark}>Hasta</PanelTableHead>
                <PanelTableHead isDark={isDark}>Doktor</PanelTableHead>
                <PanelTableHead isDark={isDark}>Agent</PanelTableHead>
                <PanelTableHead isDark={isDark}>İşlem</PanelTableHead>
              <PanelTableHead isDark={isDark}>Satışçı</PanelTableHead>
                <PanelTableHead isDark={isDark}>Durum</PanelTableHead>
                <PanelTableHead isDark={isDark} align="right">Hastane</PanelTableHead>
                <PanelTableHead isDark={isDark} align="right">Klinik</PanelTableHead>
                <PanelTableHead isDark={isDark} align="right">Toplam</PanelTableHead>
              </PanelTableHeadRow>
            </TableHeader>
            <PanelTableBody>
              {loadingOperations ? (
                <PanelTableEmpty colSpan={10} isDark={isDark} icon={Loader2} title="Yükleniyor..." />
              ) : operations.length === 0 ? (
                <PanelTableEmpty colSpan={10} isDark={isDark} icon={FileSpreadsheet} title="Kayıt yok" description="İçe Aktar sekmesinden Excel yükleyin" />
              ) : operations.map((row) => (
                <PanelTableRow key={row.id} isDark={isDark} className="cursor-pointer hover:opacity-90" onClick={() => openOperationDetail(row.id)}>
                  <PanelTableCell isDark={isDark} className="font-mono text-xs whitespace-nowrap">{formatCiroDate(row.operation_date)}</PanelTableCell>
                  <PanelTableCell isDark={isDark} className="font-medium">{row.patient_name || "—"}</PanelTableCell>
                  <PanelTableCell isDark={isDark}>{row.doctor_name || row.doctor_name_raw || "—"}</PanelTableCell>
                  <PanelTableCell isDark={isDark}>{row.agent_name || "—"}</PanelTableCell>
                  <PanelTableCell isDark={isDark} className="max-w-[10rem] truncate" title={row.procedure_name}>{row.procedure_name || "—"}</PanelTableCell>
                  <PanelTableCell isDark={isDark}>{row.seller || "—"}</PanelTableCell>
                  <PanelTableCell isDark={isDark}>
                    {row.patient_status ? (
                      <Badge variant="outline" className="text-[10px] max-w-[8rem] truncate">{row.patient_status}</Badge>
                    ) : row.has_detail ? (
                      <Badge className="text-[10px] bg-emerald-500/15 text-emerald-600 border-emerald-500/30">Detay</Badge>
                    ) : "—"}
                  </PanelTableCell>
                  <PanelTableCell isDark={isDark} align="right">{formatCiroMoney(row.hospital_payment_total)}</PanelTableCell>
                  <PanelTableCell isDark={isDark} align="right">{formatCiroMoney(row.doctor_patient_payments)}</PanelTableCell>
                  <PanelTableCell isDark={isDark} align="right" className="font-semibold text-emerald-600">{formatCiroMoney(row.total_payment)}</PanelTableCell>
                </PanelTableRow>
              ))}
            </PanelTableBody>
          </PanelTableFrame>
        </PanelTableCard>
      )}

      {activePanel === "import" && (
        <div className={`rounded-xl border p-4 space-y-4 ${cardClass}`}>
          <div className="flex flex-wrap gap-2">
            {IMPORT_STEPS.map((step) => (
              <Badge
                key={step.id}
                variant={importStep === step.id ? "default" : "outline"}
                className={`cursor-pointer ${importStep === step.id ? "bg-emerald-600" : ""}`}
                onClick={() => setImportStep(step.id)}
              >
                {step.label}
              </Badge>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {isSuperAdmin && (
              <div>
                <Label className={`text-xs ${labelClass}`}>Firma</Label>
                <Select value={importCompanyId || defaultCompanyId} onValueChange={setImportCompanyId}>
                  <SelectTrigger className={`h-8 text-xs ${inputClass}`}><SelectValue placeholder="Firma seçin" /></SelectTrigger>
                  <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                    {companies.filter((c) => c.status === 1).map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>{c.ad}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className={`text-xs ${labelClass}`}>Doktor (ciro dosyası için)</Label>
              <Select value={importDoctorId} onValueChange={setImportDoctorId}>
                <SelectTrigger className={`h-8 text-xs ${inputClass}`}><SelectValue placeholder="Doktor seçin" /></SelectTrigger>
                <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                  {doctors.map((d) => (
                    <SelectItem key={d.id} value={String(d.id)}>{d.ad_soyad}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className={`text-xs ${labelClass}`}>Hastane</Label>
              <Select value={importHospitalId || "none"} onValueChange={(v) => setImportHospitalId(v === "none" ? "" : v)}>
                <SelectTrigger className={`h-8 text-xs ${inputClass}`}><SelectValue placeholder="Hastane" /></SelectTrigger>
                <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                  <SelectItem value="none">Seçilmedi</SelectItem>
                  {hospitals.map((h) => (
                    <SelectItem key={h.id} value={String(h.id)}>{h.ad}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {importStep === "team" && (
            <div className={`rounded-lg border p-4 space-y-3 ${isDark ? "border-slate-700 bg-slate-900/40" : "border-gray-200 bg-gray-50/80"}`}>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-emerald-500" />
                <p className={`text-sm font-medium ${textClass}`}>Hoca ve ekip listesi</p>
              </div>
              <p className={`text-xs ${labelClass}`}>Hastane, doktor ve asistan kayıtlarını oluşturur.</p>
              <input ref={teamFileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => handleTeamFile(e.target.files?.[0])} />
              <Button type="button" variant="outline" onClick={() => teamFileRef.current?.click()}>
                <Upload className="w-4 h-4 mr-2" /> Dosya Seç
              </Button>
              {teamPreview && (
                <p className={`text-xs ${labelClass}`}>{teamPreview.fileName} · {teamPreview.rows.length} satır · {Math.max(0, (teamPreview.columns || 1) - 1)} hastane kolonu</p>
              )}
              <Button type="button" disabled={importing} onClick={runTeamImport} className="bg-emerald-600 hover:bg-emerald-700">
                {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Ekip Listesini Kaydet
              </Button>
            </div>
          )}

          {importStep === "workbook" && (
            <div className={`rounded-lg border p-4 space-y-3 ${isDark ? "border-slate-700 bg-slate-900/40" : "border-gray-200 bg-gray-50/80"}`}>
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4 text-emerald-500" />
                <p className={`text-sm font-medium ${textClass}`}>CEVDET DÜGER Muhasebe Excel</p>
              </div>
              <p className={`text-xs ${labelClass}`}>
                CİRO TAKİP ve ATAŞEHİR HASTA VE ÖDEME sayfaları birlikte okunur; hasta adı + tarih ile eşleştirilir.
              </p>
              <input ref={ciroFileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => handleCiroFile(e.target.files?.[0])} />
              <Button type="button" variant="outline" onClick={() => ciroFileRef.current?.click()}>
                <Upload className="w-4 h-4 mr-2" /> Muhasebe Excel Seç
              </Button>
              {opsPreview && (
                <div className={`text-xs space-y-1 ${labelClass}`}>
                  <p>
                    {opsPreview.sheetName}: <strong className={textClass}>{opsPreview.count}</strong> ciro satırı
                    {payPreview ? (
                      <> · {payPreview.sheetName}: <strong className={textClass}>{payPreview.count}</strong> ödeme satırı</>
                    ) : null}
                  </p>
                  {opsDateRange && (
                    <p>
                      Ciro tarih aralığı:{" "}
                      <span className="font-mono text-[11px]">
                        {formatCiroDate(opsDateRange.min)} – {formatCiroDate(opsDateRange.max)}
                      </span>
                    </p>
                  )}
                  {payDateRange && (
                    <p>
                      Ödeme tarih aralığı:{" "}
                      <span className="font-mono text-[11px]">
                        {formatCiroDate(payDateRange.min)} – {formatCiroDate(payDateRange.max)}
                      </span>
                    </p>
                  )}
                </div>
              )}
              <Button type="button" disabled={importing} onClick={runWorkbookImport} className="bg-emerald-600 hover:bg-emerald-700">
                {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Tümünü Aktar ve Eşleştir
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={importing}
                onClick={async () => {
                  setImporting(true);
                  try {
                    const res = await axios.post(`${apiBase}/ciro?action=link_payments`, {
                      company_id: Number(importCompanyId || defaultCompanyId),
                      doctor_id: importDoctorId ? Number(importDoctorId) : null,
                    });
                    toast.success(`${res.data?.linked || 0} kayıt eşleştirildi`);
                    fetchOperations();
                    fetchDashboard();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Eşleştirme başarısız");
                  } finally {
                    setImporting(false);
                  }
                }}
              >
                Mevcut Kayıtları Eşleştir
              </Button>
            </div>
          )}

          {batches.length > 0 && (
            <div>
              <p className={`text-xs font-medium mb-2 ${labelClass}`}>Son aktarımlar</p>
              <div className="space-y-1">
                {batches.slice(0, 5).map((b) => (
                  <div key={b.id} className={`text-xs flex justify-between ${labelClass}`}>
                    <span>{b.source_label || b.source_type} · {b.doctor_name || "—"}</span>
                    <span>+{b.inserted_count} / {b.row_count} · {formatCiroDate(String(b.created_at || "").slice(0, 10))}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className={`rounded-lg border p-4 space-y-3 ${isDark ? "border-red-900/40 bg-red-950/20" : "border-red-200 bg-red-50/60"}`}>
            <div className="flex items-center gap-2">
              <Trash2 className="w-4 h-4 text-red-500" />
              <p className={`text-sm font-medium ${textClass}`}>Ciro Verilerini Sıfırla</p>
            </div>
            <p className={`text-xs ${labelClass}`}>
              Seçili firmanın tüm ciro işlemleri, hastane ödemeleri ve import geçmişi silinir.
              {isSuperAdmin ? " Süper admin olarak tüm veritabanındaki ciro kayıtlarını da sıfırlayabilirsiniz." : ""}
            </p>

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="destructive"
                disabled={clearing}
                className="bg-red-600 hover:bg-red-700"
                onClick={() => { setClearAllDb(false); setClearDialogOpen(true); }}
              >
                {clearing && !clearAllDb ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Firma Verilerini Sil
              </Button>
              {isSuperAdmin && (
                <Button
                  type="button"
                  variant="destructive"
                  disabled={clearing}
                  className="bg-red-800 hover:bg-red-900"
                  onClick={() => { setClearAllDb(true); setClearDialogOpen(true); }}
                >
                  {clearing && clearAllDb ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Tüm DB&apos;yi Sıfırla
                </Button>
              )}
            </div>
          </div>

          <AlertDialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
            <AlertDialogContent className={isDark ? "bg-slate-900 border-slate-800 text-white" : ""}>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {clearAllDb ? "Tüm ciro veritabanı silinsin mi?" : "Firma ciro verileri silinsin mi?"}
                </AlertDialogTitle>
                <AlertDialogDescription className={labelClass}>
                  {clearAllDb
                    ? "Tüm firmalara ait ciro işlemleri, hastane ödemeleri ve import kayıtları kalıcı olarak silinecek. Bu işlem geri alınamaz."
                    : `Seçili firmanın (${importCompanyId || defaultCompanyId || "?"}) tüm ciro kayıtları silinecek. Doktor filtresi uygulanmaz — firmanın tamamı temizlenir.`}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel
                  disabled={clearing}
                  className={isDark ? "bg-slate-800 border-slate-700 text-white hover:bg-slate-700" : ""}
                >
                  Vazgeç
                </AlertDialogCancel>
                <Button
                  type="button"
                  disabled={clearing}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={runClearData}
                >
                  {clearing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Evet, Sil
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      {activePanel === "sync" && (
        <div className={`rounded-xl border p-4 space-y-4 ${cardClass}`}>
          <div className={`rounded-lg border p-3 space-y-2 ${isDark ? "border-slate-700 bg-slate-900/50" : "border-amber-200 bg-amber-50/80"}`}>
            <p className={`text-xs font-semibold ${isDark ? "text-amber-300" : "text-amber-800"}`}>Google Sheet erişimi (401 hatası çözümü)</p>
            <p className={`text-xs leading-relaxed ${labelClass}`}>
              Sheet özel olduğu için CSV export çalışmaz. <strong>Service Account</strong> kurun:
            </p>
            <ol className={`text-xs leading-relaxed list-decimal pl-4 space-y-1 ${labelClass}`}>
              <li><a href="https://console.cloud.google.com/apis/library/sheets.googleapis.com" target="_blank" rel="noreferrer" className="underline">Google Sheets API</a> etkinleştirin</li>
              <li>Service Account oluşturup JSON key indirin</li>
              <li>Dosyayı sunucuya kaydedin: <code className="text-[10px">backend/api/secrets/google-service-account.json</code></li>
              <li>Google Sheet&apos;te <strong>Paylaş</strong> → aşağıdaki e-postayı <strong>Görüntüleyen</strong> olarak ekleyin</li>
            </ol>
            {googleStatus?.client_email ? (
              <div className={`mt-2 p-2 rounded border text-xs font-mono break-all ${isDark ? "border-emerald-700/50 bg-emerald-500/10 text-emerald-300" : "border-emerald-200 bg-white text-emerald-800"}`}>
                {googleStatus.client_email}
              </div>
            ) : (
              <p className={`text-xs ${isDark ? "text-red-400" : "text-red-600"}`}>
                {googleStatus?.message || "Service Account henüz yapılandırılmamış"}
              </p>
            )}
            {googleStatus?.configured && googleStatus?.message && (
              <p className={`text-[10px] ${labelClass}`}>{googleStatus.message}</p>
            )}
          </div>

          <p className={`text-xs ${labelClass}`}>
            Günlük senkron yalnızca yeni satırları ekler (hash ile tekrar engellenir).
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div>
              <Label className={`text-xs ${labelClass}`}>Etiket</Label>
              <Input value={sheetForm.label} onChange={(e) => setSheetForm((p) => ({ ...p, label: e.target.value }))} className={`h-8 text-xs ${inputClass}`} />
            </div>
            <div>
              <Label className={`text-xs ${labelClass}`}>Sheet tipi</Label>
              <Select value={sheetForm.sheet_type} onValueChange={(v) => setSheetForm((p) => ({ ...p, sheet_type: v }))}>
                <SelectTrigger className={`h-8 text-xs ${inputClass}`}><SelectValue /></SelectTrigger>
                <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                  <SelectItem value="ciro_takip">Ciro Takip</SelectItem>
                  <SelectItem value="hospital_payments">Hastane Ödemeleri</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="lg:col-span-2">
              <Label className={`text-xs ${labelClass}`}>Google Sheet URL</Label>
              <Input value={sheetForm.sheet_url} onChange={(e) => setSheetForm((p) => ({ ...p, sheet_url: e.target.value }))} className={`h-8 text-xs ${inputClass}`} />
            </div>
            <div>
              <Label className={`text-xs ${labelClass}`}>Doktor</Label>
              <Select value={sheetForm.doctor_id} onValueChange={(v) => setSheetForm((p) => ({ ...p, doctor_id: v }))}>
                <SelectTrigger className={`h-8 text-xs ${inputClass}`}><SelectValue placeholder="Doktor" /></SelectTrigger>
                <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                  {doctors.map((d) => (
                    <SelectItem key={d.id} value={String(d.id)}>{d.ad_soyad}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className={`text-xs ${labelClass}`}>Hastane</Label>
              <Select value={sheetForm.hospital_id || "none"} onValueChange={(v) => setSheetForm((p) => ({ ...p, hospital_id: v === "none" ? "" : v }))}>
                <SelectTrigger className={`h-8 text-xs ${inputClass}`}><SelectValue /></SelectTrigger>
                <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                  <SelectItem value="none">Seçilmedi</SelectItem>
                  {hospitals.map((h) => (
                    <SelectItem key={h.id} value={String(h.id)}>{h.ad}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setSheetForm((p) => ({ ...p, ...CEVDET_DEFAULT_SHEETS.operations }))}>
              Cevdet Ciro URL
            </Button>
            <Button type="button" disabled={importing} onClick={saveSheetSource} className="bg-emerald-600 hover:bg-emerald-700">
              Kaynağı Kaydet
            </Button>
          </div>

          <div className="space-y-2">
            {sheetSources.length === 0 ? (
              <p className={`text-sm ${labelClass}`}>Henüz kayıtlı sheet yok</p>
            ) : sheetSources.map((src) => (
              <div key={src.id} className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg border p-3 ${isDark ? "border-slate-700" : "border-gray-200"}`}>
                <div>
                  <p className={`text-sm font-medium ${textClass}`}>{src.label}</p>
                  <p className={`text-xs ${labelClass}`}>{src.doctor_name} · {src.sheet_type} · gid={src.gid}</p>
                  {src.last_sync_at && (
                    <p className={`text-[10px] ${labelClass}`}>Son: {src.last_sync_at} · {src.last_sync_status}</p>
                  )}
                </div>
                <Button type="button" size="sm" variant="outline" disabled={syncingId === src.id} onClick={() => syncSheet(src.id)}>
                  {syncingId === src.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CloudDownload className="w-4 h-4 mr-1" />}
                  Senkron Et
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <CiroOperationDetailModal
        open={detailOpen}
        onOpenChange={setDetailOpen}
        loading={detailLoading}
        data={detailData}
        isDark={isDark}
        textClass={textClass}
        labelClass={labelClass}
      />
    </div>
  );
};

export default CiroTrackingPage;
